import { useMemo } from 'react'
import { cn } from '@/utils'
import { ProjectDocumentTitle } from '@/components/core/ProjectDocumentTitle'
import { BookIcon } from '@/components/icons/BookIcon'
import { ListBulletIcon } from '@/components/icons/ListBulletIcon'
import { DocumentTextIcon } from '@/components/icons/DocumentTextIcon'
import { useTechnicianPages } from '../hooks/useTechnicianDocument'
import { useTechnicianPageStore } from '../hooks/useTechnicianPageStore'
import { useTechnicianDocument } from '../hooks/useTechnicianDocument'
import { CheckCircleIcon } from '@heroicons/react/24/solid'

import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'

// Menu items.

export const PageSideNavigation = () => {
  const { state } = useSidebar()
  const { currentPage } = useTechnicianDocument()

  const {
    data: { pages },
  } = useTechnicianPages()

  const visitedPages = useTechnicianPageStore((state) => state.visitedPages)
  const currentTechnician = useTechnicianPageStore(
    (state) => state.currentTechnician,
  )

  const items = useMemo(() => {
    const menuItems = (pages || []).map((page, i) => {
      const Icon = {
        title: BookIcon,
        'parts-bom': ListBulletIcon,
        'tools-bom': ListBulletIcon,
        unknown: DocumentTextIcon,
      }[page.type ?? 'unknown']

      const title = {
        title: 'Cover',
        'parts-bom': 'Bill of Materials',
        'tools-bom': 'Tools Bill of Materials',
        unknown: page.name || `Operation ${page.order_number || i + 1}`,
      }[page.type ?? 'unknown']

      return {
        id: page.id,
        title,
        order: page.order || '0',
        url: '#',
        icon: Icon,
      }
    })

    return menuItems
  }, [pages])

  return (
    <Sidebar collapsible="icon" className="bg-white">
      <SidebarHeader>
        <div className="flex justify-end">
          <SidebarTrigger />
        </div>
        <div
          className={cn('mb-4 px-10 text-black', {
            hidden: state === 'collapsed',
          })}
        >
          <ProjectDocumentTitle isTechnicianMode isReadOnly />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu>
          <div
            className={cn('w-full', {
              'px-2': state === 'collapsed',
              'px-4': state === 'expanded',
            })}
          >
            {items.map((item, i) => {
              const Icon = item.icon as any
              return (
                <SidebarMenuItem key={`${i}-${item.title}`}>
                  <SidebarMenuButton
                    asChild
                    isActive={false}
                    // Add tooltip to show when sidebar is collapsed
                    tooltip={item.title}
                  >
                    <a
                      href={item.url}
                      className={cn('text-black font-medium', {
                        'bg-indigo-100':
                          currentTechnician && currentPage.id === item.id,
                      })}
                    >
                      <CheckCircleIcon
                        className={cn('h-4 w-4 text-green-500', {
                          'absolute bottom-0 right-0': state === 'collapsed',
                        })}
                        style={{
                          visibility:
                            item?.id && visitedPages.includes(item.id)
                              ? 'visible'
                              : 'hidden',
                        }}
                      />
                      <Icon className="h-5 w-5" />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })}
          </div>
        </SidebarMenu>
      </SidebarContent>
    </Sidebar>
  )
}
